import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';
import { CPListParams } from '@/types';
import { IBlock } from './commercial-proposals/blocks.store';

export type TShopStatus = {
  enabled: boolean;
  usedOn: string[];
  deliveredAt: string | null;
  deliveredRevision: string | null;
  pending: boolean;
  lastError: string | null;
};

export type TCommercialProposal = {
  id: number;
  uuid: string;
  name: string;
  jurisdiction: string;
  disclaimer: string;
  data: any;
  currency: string;
  updatedAt: string;
  isDraft: boolean;
  shopEnabled?: boolean;
  shop?: TShopStatus | null;
};

export type TPaginatedCPResponse = {
  proposals: TCommercialProposal[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

const endpoint = 'commercial-proposal';

const keys = {
  all: [endpoint] as const,
  detail: (id: number) => [...keys.all, id] as const,
  blocks: (id: number) => [...keys.all, id, 'blocks'] as const,
};

export const useListCommercialProposals = (params: CPListParams) =>
  useQuery(
    {
      queryKey: [...keys.all, 'paginated', params] as const,
      queryFn: () =>
        call<TPaginatedCPResponse>(`${endpoint}/list`, {
          json: { ...params },
        }).then((res) => res.json()),
    },
    qc,
  );

export const useCommercialProposal = (id: number) =>
  useQuery(
    {
      queryKey: keys.detail(id),
      queryFn: () =>
        call<TCommercialProposal>(`${endpoint}/get`, {
          json: { id },
        }).then((res) => res.json()),
      enabled: id > 0,
    },
    qc,
  );

export const useCreateCommercialProposal = () =>
  useMutation(
    {
      mutationFn: (
        json: Omit<
          TCommercialProposal,
          'id' | 'uuid' | 'data' | 'disclaimer' | 'updatedAt' | 'isDraft'
        >,
      ) =>
        call<TCommercialProposal>(`${endpoint}/add`, {
          json,
        }).then((res) => res.json()),
      onSuccess: (added) => {
        qc.setQueryData(keys.all, (old: TCommercialProposal[]) => [
          added,
          ...(old ?? []),
        ]);
        qc.setQueryData(keys.detail(added.id), added);
      },
    },
    qc,
  );

/** An offer a proposal edit would disturb, as reported by the save endpoint. */
export interface TProposalSplitImpact {
  offerId: number;
  offerTitle: string | null;
  contactName: string | null;
  accountName: string | null;
  planStatus: string | null;
}

export class SplitPlansAffectedError extends Error {
  /** The editor opens a confirmation dialog for this, so no generic error toast. */
  readonly handled = true;

  constructor(
    readonly offers: TProposalSplitImpact[],
    message: string,
  ) {
    super(message);
    this.name = 'SplitPlansAffectedError';
  }
}

/**
 * The API refuses the save with 409 when offers are still selecting from this proposal,
 * listing them. The shared client rejects with the response body as text, so it is
 * parsed back here and re-thrown typed for the confirmation dialog to render.
 */
const asSplitPlansAffected = (e: unknown): never => {
  const raw = typeof e === 'string' ? e : (e as Error)?.message;
  try {
    const body = JSON.parse(raw);
    if (body?.error === 'split-plans-affected') {
      throw new SplitPlansAffectedError(body.offers ?? [], body.message ?? '');
    }
  } catch (parsed) {
    if (parsed instanceof SplitPlansAffectedError) throw parsed;
  }
  throw e;
};

export const useSaveCommercialProposal = (id: number) =>
  useMutation(
    {
      mutationFn: ({
        blocks,
        name,
        disclaimer,
        renewableServiceOrder,
        invalidateSplitPlans,
        acknowledgedOfferIds,
      }: {
        blocks: IBlock[];
        name: string | null;
        disclaimer: string | null;
        renewableServiceOrder?: number[] | null;
        invalidateSplitPlans?: boolean;
        acknowledgedOfferIds?: number[];
      }) =>
        call<void>(`${endpoint}/save`, {
          json: {
            id,
            blocks: blocks as any,
            name,
            disclaimer,
            renewableServiceOrder,
            invalidateSplitPlans,
            acknowledgedOfferIds,
          },
        }).catch(asSplitPlansAffected),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.blocks(id) });
        qc.invalidateQueries({ queryKey: keys.detail(id) });
      },
    },
    qc,
  );

export const useCommercialProposalsBlocks = (id: number) =>
  useQuery(
    {
      queryKey: keys.blocks(id),
      queryFn: () =>
        call<IBlock[]>(`${endpoint}/list-blocks`, {
          json: { cpId: id },
        }).then((res) => res.json()),
    },
    qc,
  );

export const useDeleteCommercialProposal = () =>
  useMutation(
    {
      mutationFn: (id: number) =>
        call(`${endpoint}/delete`, {
          json: { id },
        }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );

export const usePublishCommercialProposal = () =>
  useMutation(
    {
      mutationFn: (id: number) =>
        call(`${endpoint}/publish`, {
          json: { id },
        }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );

export const useUnpublishCommercialProposal = () =>
  useMutation(
    {
      mutationFn: (id: number) =>
        call(`${endpoint}/unpublish`, {
          json: { id },
        }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );

export const useReSendCommercialProposal = () =>
  useMutation(
    {
      mutationFn: (json: { offerId: number }) =>
        call<void>(`commercial-proposal/re-send`, { json }),
      onSuccess: () => {},
    },
    qc,
  );

export const useShopEnable = (id: number) =>
  useMutation(
    {
      mutationFn: () => call(`${endpoint}/shop-enable`, { json: { id } }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.detail(id) });
      },
    },
    qc,
  );

export const useShopDisable = (id: number) =>
  useMutation(
    {
      mutationFn: () => call(`${endpoint}/shop-disable`, { json: { id } }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: keys.detail(id) });
      },
    },
    qc,
  );
